import React from 'react';
import styled, { withTheme, type DefaultTheme } from 'styled-components';
import type { ExtensionLandingPageQuery } from '@graphql/pages/extension.generated';
import type { Locale } from '@lib/locale';
import HeroBannerSection from '../shared/HeroBannerSection';
import HelpSection from '../shared/HelpSection';
import NotificationSection from '../shared/NotificationSection';
import BannerSection from '../shared/BannerSection';
import { getBannerTracking } from '../../../../lib/helpers/bannerTracking.helpers';
import { DATA_TRACK_CLICK_HERO_BANNER_SECTION } from '../../../../lib/constants';
import { toSlug } from '../../../../lib/utils';

const SectionComponents = {
  HeroBannerSection,
  HelpSection,
  NotificationSection,
  BannerSection,
};

type Props = {
  theme: DefaultTheme;
  locale: Locale;
  pageData: ExtensionLandingPageQuery['extensionInstallation'];
  client: string | undefined;
};

type SectionProps = Record<string, unknown> & {
  buttonText?: string | null;
  buttonUrl?: string | null;
  title?: string | null;
  subtitle?: string | null;
};

const browserLink = (config: unknown, client: string | undefined) => {
  if (!client || !config || typeof config !== 'object' || Array.isArray(config)) return {};
  const value: unknown = Object.getOwnPropertyDescriptor(config, client)?.value;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const buttonText = 'buttonText' in value && typeof value.buttonText === 'string' ? value.buttonText : undefined;
  const buttonUrl = 'buttonUrl' in value && typeof value.buttonUrl === 'string' ? value.buttonUrl : undefined;
  return { buttonText, buttonUrl };
};

const ExtensionLandingContainer = styled.div`
  h1, h2, h3, h4, h5, p, span, ul, li {
    margin: 0;
    padding: 0;
  }

  button, a {
    @media screen and (max-width: ${(p) => p.theme.breakpoint.mobile}px) {
      width: 100%;
    }
  }

  .content-section,
  .content-section--hero {
    width: 100%;
    max-width: 100vw;
    padding: 48px 16px;

    @media (min-width: ${(p) => p.theme.minWidthBreakpoint.tablet}px) {
      max-width: 85vw;
      padding: 48px 0;
    }
    
    @media (min-width: ${(p) => p.theme.minWidthBreakpoint.desktop}px) {
      width: 1224px;
      padding: 80px 108px;
    }
  }
`;

export const ExtensionLanding = (props: Props) => {
  const {
    pageData,
    client,
  } = props;

  const contentSections = pageData?.contentSections || [];
  const extensionBrowserLinkConfig = pageData?.extensionBrowserLinkConfig;

  const {
    buttonText,
    buttonUrl,
  } = browserLink(extensionBrowserLinkConfig, client);

  return (
    <ExtensionLandingContainer>
      {contentSections.filter((section) => section !== null).map((section) => {
        const SectionComponent = Object.entries(SectionComponents)
          .find(([name]) => name === section.renderComponent)?.[1] ?? null;
        const sectionProps: SectionProps = { ...section };
        switch (section.renderComponent) {
          case 'HeroBannerSection': {
            const ctaButtonText = buttonText || sectionProps.buttonText;
            const ctaButtonUrl = buttonUrl || sectionProps.buttonUrl;

            sectionProps.heroImageUrl = section.imageUrl;
            sectionProps.portraitTabletHeroImageUrl = section.imageUrl;
            sectionProps.classNames = 'extension-landing';
            sectionProps.heading = sectionProps.title;
            sectionProps.subheading = sectionProps.subtitle;

            if (ctaButtonText || ctaButtonUrl) {
              sectionProps.buttons = [{
                buttonText: ctaButtonText,
                variant: 'primary',
                size: 'medium',
                theme: 'light',
                url: ctaButtonUrl,
                rel: 'noopener noreferrer',
                target: '_blank',
                bannerTracking: getBannerTracking({
                  bannerImgUrl: section.imageUrl,
                  bannerLocation: DATA_TRACK_CLICK_HERO_BANNER_SECTION,
                  position: undefined,
                  linkName: toSlug(ctaButtonText ?? undefined),
                }),
              }];
            }

            break;
          }
          case 'BannerSection':
            if (buttonText || buttonUrl) {
              sectionProps.buttonText = buttonText || sectionProps.buttonText;
              sectionProps.buttonUrl = buttonUrl || sectionProps.buttonUrl;
            }
            break;
          default:
            break;
        }

        return (SectionComponent
           && (
           <SectionComponent
             key={section.name || section.renderComponent}
             browserConfig={extensionBrowserLinkConfig}
             {...sectionProps}
           />
           ));
      })}
    </ExtensionLandingContainer>
  );
};

export default withTheme(ExtensionLanding);
